// Typed client for /api/ForexTrading/*. Mirrors the controller's DTOs.
// Follows the project pattern: API_BASE + apiFetch (auth + base URL come
// from there); no shared "ApiService" object.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

export interface FxcmHealth {
  enabled: boolean;
  brokerReachable: boolean;
  accountId: string | null;
  message: string;
}

export interface FxcmSettings {
  username: string;
  passwordMasked: string;       // "**** set" | "**** unset"
  connectionType: 'Demo' | 'Real';
  tradingServerUrl: string;
  accountId: string;
  tradingEnabled: boolean;
  updatedAt: string | null;
}

export interface FxcmAccount {
  accountId: string;
  status: string;
  balance: number;
  equity: number;
  usedMargin: number;
  usableMargin: number;
  marginLevelPercent: number | null;
  isLocked: boolean;
  dayPL: number;        // today's realized + unrealized P/L (account currency)
  grossPL: number;      // open-position P/L (account currency)
  maintMarginPercent: number | null;   // usable maintenance-margin %
}

export interface FxcmPosition {
  tradeId: string;      // FXCM ticket id — required to modify this trade's SL/TP
  symbol: string;       // internal form, e.g. EURUSD
  side: 'long' | 'short';
  amount: number;       // contract units (NOT lots — UI converts at display)
  openRate: number;
  currentRate: number | null;
  unrealizedPnL: number | null;   // account currency
  unrealizedPnLPercent: number | null;
  stop: number | null;  // attached stop-loss rate (null = none)
  limit: number | null; // attached take-profit rate (null = none)
  pips: number | null;       // live P/L in pips
  rollover: number | null;   // accrued swap/financing (account currency)
  commission: number | null; // commission booked on the open trade
  openedAt: string | null;   // when the trade was opened (ISO UTC)
}

export interface FxcmOrder {
  orderId: string;      // FXCM order id (string — used for cancel)
  symbol: string;
  side: string;
  amount: number;
  status: string;
  rate: number;
  type: string;         // FXCM order-type code (S/L/SE/LE/RE/…)
  timeInForce: string;
  expireAt: string | null;  // GTD expiry
  tradeId: string;          // attached trade (entry orders → "")
  submittedAt: string | null;
}

export interface FxcmClosedTrade {
  tradeId: string;
  symbol: string;       // internal form, e.g. USDJPY
  side: 'long' | 'short';
  amount: number;       // contract units
  openRate: number;
  closeRate: number;
  grossPL: number;      // realized P/L in the account currency (FXCM-converted)
  closeTime: string;    // ISO UTC
  netPL: number;        // gross + commission + rollover + dividends (account currency)
  pipPL: number;        // FXCM pip P/L (0 when only the snapshot/mirror source had it)
  commission: number;
  rolloverInterest: number;
  dividends: number;
  openTime: string;     // ISO UTC
  closeReason: '' | 'stop' | 'takeProfit' | 'manual' | 'marginCall';
  closeReasonIsGuess: boolean; // true = inferred by rate-match; false = authoritative
}

/** Real-time trades payload pushed over the trading hub (ForexTrades event). */
export interface FxcmTradesPayload {
  positions: FxcmPosition[];
  closedTrades: FxcmClosedTrade[];
}

/** One page of persisted closed-trade history (GET /history). */
export interface FxcmTradeHistoryPage {
  items: FxcmClosedTrade[];
  total: number;
  page: number;
  pageSize: number;
}

/** A historical OHLC candle (mid price) for the inline chart. */
export interface FxcmCandle {
  timestampUtc: string;   // candle open time (ISO UTC)
  open: number;
  high: number;
  low: number;
  close: number;
  tickQty: number;
}

/** FXCM timeframe codes the chart endpoint accepts, with display labels. */
export const FOREX_CHART_PERIODS: { code: string; label: string }[] = [
  { code: 'm1', label: '1m' }, { code: 'm5', label: '5m' },
  { code: 'm15', label: '15m' }, { code: 'm30', label: '30m' },
  { code: 'H1', label: '1h' }, { code: 'H4', label: '4h' },
  { code: 'D1', label: '1D' }, { code: 'W1', label: '1W' },
];

export interface UpdateFxcmSettings {
  username?: string;
  password?: string;       // blank preserves existing
  tradingServerUrl?: string;
  connectionType?: 'Demo' | 'Real';
  accountId?: string;
  tradingEnabled?: boolean;
}

export interface PlaceMarketOrder {
  symbol: string;
  side: 'buy' | 'sell' | 'long' | 'short';
  contracts: number;      // FXCM contracts — 1 forex contract = 1,000 units (one micro lot)
  stopPrice?: number;     // optional attached stop-loss rate
  limitPrice?: number;    // optional attached take-profit rate
  rateMin?: number;       // Market Range band low (both → ranged order)
  rateMax?: number;       // Market Range band high
  trailStepPips?: number; // makes the attached stop a trailing stop
  timeInForce?: 'IOC' | 'FOK';
}

export interface PlaceEntryOrder {
  symbol: string;
  side: 'buy' | 'sell' | 'long' | 'short';
  contracts: number;      // FXCM contracts — 1 forex contract = 1,000 units (one micro lot)
  orderType: 'limit' | 'stop' | 'range'; // LE / SE / RE
  entryPrice: number;     // trigger rate the position opens at
  stopPrice?: number;     // optional attached stop-loss rate
  limitPrice?: number;    // optional attached take-profit rate
  rateMin?: number;       // Range Entry band low (required for 'range')
  rateMax?: number;       // Range Entry band high
  trailStepPips?: number; // makes the attached stop a trailing stop
  timeInForce?: 'GTC' | 'DAY' | 'GTD';
  expireAt?: string;      // ISO date/time — required when timeInForce = GTD
}

export interface ModifyProtectiveOrders {
  // leave-unchanged on omit; the Clear flags remove that side. Setting a rate
  // and clearing the same side is rejected server-side.
  rateStop?: number;
  rateLimit?: number;
  clearStop?: boolean;
  clearLimit?: boolean;
}

export interface ProtectiveOrdersResult {
  message: string;
  stopApplied: boolean;
  limitApplied: boolean;
}

export interface FxcmInstrument {
  symbol: string;             // internal no-slash form, e.g. EURUSD / XAUUSD
  displaySymbol: string;      // FXCM form, e.g. EUR/USD / XAU/USD / US30
  instrumentClass: string;    // forex | indices | commodity | bullion | crypto | …
  contractMultiplier: number; // FXCM price/P&L multiplier (NOT a lot size)
  pointSize: number;          // smallest price increment
  isTradingAllowed: boolean;
  baseUnitSize: number;       // account units-per-lot — order units = baseUnitSize × lots (0 = unknown)
}

export interface FxcmQuote {
  symbol: string;             // internal form, e.g. EURUSD
  bid: number;
  ask: number;
  spread: number;             // ask - bid, in raw rate units
  timestampUtc: string;
  high: number;               // session day high (0 = unavailable)
  low: number;                // session day low
}

// Unwrap the API's {error} shape so users see the message, not a raw JSON blob.
async function readError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const body = JSON.parse(text);
    if (body?.error) return String(body.error);
  } catch { /* not JSON */ }
  return text || `Request failed (${res.status})`;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<T>;
}

async function sendJson<T>(path: string, method: 'POST' | 'PUT', body?: unknown): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body == null ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res));
  // Some endpoints return 204 / empty — guard against JSON parse failure.
  const text = await res.text();
  return (text ? JSON.parse(text) : ({} as T)) as T;
}

// Demo and live forex are the SAME API shape on two different controllers
// (api/ForexTrading vs api/ForexLiveTrading). createForexService binds the
// client to one route surface; the panel takes whichever it needs as a prop
// so a demo panel can never accidentally hit the live (real-money) account.
function createForexService(apiBase: string) {
  return {
    health: () => getJson<FxcmHealth>(`${apiBase}/health`),
    getSettings: () => getJson<FxcmSettings>(`${apiBase}/settings`),
    updateSettings: (req: UpdateFxcmSettings) =>
      sendJson<FxcmSettings>(`${apiBase}/settings`, 'PUT', req),
    setEnabled: (tradingEnabled: boolean) =>
      sendJson<FxcmSettings>(`${apiBase}/config`, 'PUT', { tradingEnabled }),
    getAccount: () => getJson<FxcmAccount>(`${apiBase}/account`),
    getPositions: () => getJson<FxcmPosition[]>(`${apiBase}/positions`),
    closePosition: (symbol: string) =>
      sendJson<{ message: string }>(
        `${apiBase}/positions/${encodeURIComponent(symbol)}/close`, 'POST'),
    setProtectiveOrders: (tradeId: string, req: ModifyProtectiveOrders) =>
      sendJson<ProtectiveOrdersResult>(
        `${apiBase}/positions/${encodeURIComponent(tradeId)}/protective-orders`, 'PUT', req),
    getRecentOrders: (limit = 50) =>
      getJson<FxcmOrder[]>(`${apiBase}/orders/recent?limit=${limit}`),
    getRecentTrades: (limit = 50) =>
      getJson<FxcmClosedTrade[]>(`${apiBase}/trades?limit=${limit}`),
    getCandles: (symbol: string, period = 'H1', count = 200) =>
      getJson<FxcmCandle[]>(
        `${apiBase}/candles?symbol=${encodeURIComponent(symbol)}&period=${period}&count=${count}`),
    getHistory: (page = 1, pageSize = 25) =>
      getJson<FxcmTradeHistoryPage>(`${apiBase}/history?page=${page}&pageSize=${pageSize}`),
    syncStatement: (months = 3) =>
      sendJson<{ parsed: number; inserted: number }>(`${apiBase}/sync?months=${months}`, 'POST'),
    getInstruments: () => getJson<FxcmInstrument[]>(`${apiBase}/instruments`),
    getQuotes: () => getJson<FxcmQuote[]>(`${apiBase}/quotes`),
    placeMarketOrder: (req: PlaceMarketOrder) =>
      sendJson<{ orderId: string; symbol: string; side: string; contracts: number }>(
        `${apiBase}/orders/market`, 'POST', req),
    placeEntryOrder: (req: PlaceEntryOrder) =>
      sendJson<{ orderId: string; symbol: string; side: string; orderType: string; contracts: number; entryPrice: number }>(
        `${apiBase}/orders/entry`, 'POST', req),
    cancelOrder: (orderId: string) =>
      sendJson<{ message: string; orderId: string }>(
        `${apiBase}/orders/${encodeURIComponent(orderId)}/cancel`, 'POST'),
  };
}

/** Shared shape of the demo / live forex clients. */
export type ForexService = ReturnType<typeof createForexService>;

/** Demo — FXCM practice account. Drives the Paper Trading → Forex tab. */
export const forexService = createForexService('/api/ForexTrading');

/** Live — funded FXCM account. REAL MONEY. Drives the Live Trading page. */
export const forexLiveService = createForexService('/api/ForexLiveTrading');

/** Human label for an instrument-class code from the API. */
export function instrumentClassLabel(c: string): string {
  switch (c) {
    case 'forex': return 'Forex';
    case 'indices': return 'Indices';
    case 'commodity': return 'Commodities';
    case 'bullion': return 'Metals';
    case 'crypto': return 'Crypto';
    case 'treasury': return 'Treasuries';
    case 'shares': case 'cfdshares': return 'Shares';
    case 'fxindex': return 'FX Index';
    default: return 'Other';
  }
}

// ── Display helpers ─────────────────────────────────────────────

/** Format an internal forex symbol (EURUSD) with the standard slash for
 * display (EUR/USD). Equity tickers and unknowns return unchanged. */
export function fmtForexSymbol(s: string): string {
  if (!s || s.length !== 6) return s;
  return `${s.slice(0, 3)}/${s.slice(3, 6)}`;
}

// (contractToLots / lotsToContracts removed — they encoded an obsolete
// hardcoded 100k/1k lot heuristic. Sizing now uses the instrument's
// FXCM baseUnitSize: order units = baseUnitSize × contracts.)
